import React from 'react'
import { Link } from 'react-router-dom'
import { Check, Copy } from 'lucide-react'
import { api } from '../lib/api'
import { useAuth } from '../auth/AuthContext'
import { Roles } from '../auth/rbac'
import { INSURER_SYSTEMS_SECTIONS } from '../pages/insurerSystemsData'
import { resolveInsurerLogoUrlFromOrgOptions } from '../lib/insurerLogoFromOrgOptions'
import { mergeInsurerSystemSections } from '../lib/insurerSystemProfileUtils'
import MenuGuideButton from './MenuGuideButton'

const FAVORITES_STORAGE_KEY = 'sgez.insurerSystems.favorites.v1'
const INSURER_TYPE_TABS = [
  { id: 'all', label: '전체' },
  { id: 'fire', label: '손해보험사' },
  { id: 'life', label: '생명보험사' },
  { id: 'mutual', label: '공제' },
]

function migrateInsurerSystemStorageKey(key) {
  if (typeof key !== 'string' || !key.startsWith('nonlife::')) return key
  return `fire::${key.slice('nonlife::'.length)}`
}

function StarButton({ pressed, onClick, label }) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={pressed}
      onClick={onClick}
      className={
        pressed
          ? 'inline-flex h-9 w-9 items-center justify-center rounded-full bg-amber-50 text-amber-600 ring-1 ring-amber-200 hover:bg-amber-100'
          : 'inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-400 ring-1 ring-slate-200 hover:bg-slate-50 hover:text-slate-600'
      }
      title={label}
    >
      <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5">
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.956a1 1 0 0 0 .95.69h4.157c.969 0 1.371 1.24.588 1.81l-3.363 2.444a1 1 0 0 0-.364 1.118l1.286 3.956c.3.921-.755 1.688-1.539 1.118l-3.363-2.444a1 1 0 0 0-1.175 0l-3.363 2.444c-.783.57-1.838-.197-1.539-1.118l1.286-3.956a1 1 0 0 0-.364-1.118L2.07 9.383c-.783-.57-.38-1.81.588-1.81h4.157a1 1 0 0 0 .95-.69l1.286-3.956Z" />
      </svg>
    </button>
  )
}

function LinkList({ items, variant = 'stack' }) {
  const list = Array.isArray(items) ? items : []
  if (list.length === 0) return <span className="text-slate-400">-</span>

  const wrapClass = variant === 'inline' ? 'flex flex-wrap gap-2' : 'flex flex-col gap-1.5'
  return (
    <div className={wrapClass}>
      {list.map((it, idx) => {
        const key = `${it?.text || 'item'}-${idx}`
        const href = it?.href
        if (!href) return <span key={key} className="text-slate-700">{it?.text || '-'}</span>
        const isTel = String(href).startsWith('tel:')
        return (
          <a
            key={key}
            href={href}
            className={
              variant === 'inline'
                ? (isTel
                  ? 'inline-flex items-center rounded-full bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-white hover:text-brand-sky'
                  : 'inline-flex items-center rounded-full bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-white hover:text-brand-sky')
                : (isTel
                  ? 'text-slate-700 hover:text-brand-sky'
                  : 'text-slate-700 hover:text-brand-sky underline-offset-2 hover:underline')
            }
            target={isTel ? undefined : '_blank'}
            rel={isTel ? undefined : 'noreferrer noopener'}
          >
            {it?.text || '열기'}
          </a>
        )
      })}
    </div>
  )
}

function ExternalButton({ href, label }) {
  if (!href) return <span className="text-slate-400">-</span>
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className="inline-flex items-center justify-center rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50"
    >
      {label}
    </a>
  )
}

function AllCredentialsModal({ open, sections, onClose, onOpenCredential }) {
  const [showPw, setShowPw] = React.useState(false)
  const [copiedKey, setCopiedKey] = React.useState('')

  React.useEffect(() => {
    if (!open) {
      setShowPw(false)
      setCopiedKey('')
    }
  }, [open])

  const copyRow = React.useCallback(async (row) => {
    const parts = [row.username, row.password].filter(Boolean)
    if (parts.length === 0) return
    try {
      await navigator.clipboard.writeText(parts.join('\n'))
      setCopiedKey(row.key)
      window.setTimeout(() => {
        setCopiedKey((current) => (current === row.key ? '' : current))
      }, 1200)
    } catch {
      // ignore
    }
  }, [])

  if (!open) return null

  const allRows = (Array.isArray(sections) ? sections : []).flatMap((sec) => sec.items || [])
  const registeredCount = allRows.filter((r) => r.username || r.password).length
  const totalCount = allRows.length

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="all-credentials-title"
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-6xl flex-col rounded-t-2xl border bg-white shadow-xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b px-4 py-4 sm:px-5">
          <div>
            <div id="all-credentials-title" className="text-lg font-bold text-slate-900">ID/PW 전체보기</div>
            <div className="mt-0.5 text-xs text-slate-500">
              등록 {registeredCount}건 · 전체 {totalCount}건
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="rounded-lg border bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
              onClick={() => setShowPw((v) => !v)}
            >
              {showPw ? '비밀번호 숨기기' : '비밀번호 표시'}
            </button>
            <button type="button" className="rounded-lg px-2 py-1 text-sm text-slate-500 hover:bg-slate-50" onClick={onClose}>
              닫기
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3 sm:px-5">
          {totalCount === 0 ? (
            <div className="rounded-xl border bg-slate-50 px-4 py-8 text-center text-sm text-slate-600">
              표시할 보험사가 없습니다.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              {(Array.isArray(sections) ? sections : []).map((sec) => (
                <div key={sec.id} className="flex min-h-[240px] flex-col overflow-hidden rounded-xl border bg-white">
                  <div className="border-b bg-slate-50 px-3 py-2.5">
                    <div className="font-semibold text-slate-900">{sec.title}</div>
                    <div className="mt-0.5 text-[11px] text-slate-500">{sec.items?.length || 0}개</div>
                  </div>
                  <div className="min-h-0 flex-1 overflow-y-auto">
                    <div className="sticky top-0 grid grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)_minmax(0,1fr)_2rem] gap-2 border-b bg-slate-50 px-3 py-2 text-[11px] font-semibold text-slate-500">
                      <span>보험사</span>
                      <span>ID</span>
                      <span>PW</span>
                      <span className="sr-only">복사</span>
                    </div>
                    <div className="divide-y">
                      {(sec.items || []).map((row) => {
                        const hasCred = Boolean(row.username || row.password)
                        return (
                          <div
                            key={row.key}
                            className="grid grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)_minmax(0,1fr)_2rem] gap-2 px-3 py-2 text-xs items-center"
                          >
                            <span className="min-w-0 truncate font-medium text-slate-900" title={row.insurerName}>
                              {row.insurerName}
                            </span>
                            {hasCred ? (
                              <span className="min-w-0 break-all font-mono text-slate-800" title={row.username || undefined}>
                                {row.username || '-'}
                              </span>
                            ) : (
                              <button
                                type="button"
                                className="min-w-0 text-left font-semibold text-slate-500 hover:text-brand-sky hover:underline"
                                onClick={() => {
                                  onClose?.()
                                  onOpenCredential?.(row.sectionId, row.item)
                                }}
                              >
                                미등록
                              </button>
                            )}
                            {hasCred ? (
                              <span className="min-w-0 break-all font-mono text-slate-800" title={showPw && row.password ? row.password : undefined}>
                                {row.password ? (showPw ? row.password : '••••••••') : '-'}
                              </span>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                            {hasCred ? (
                              <button
                                type="button"
                                aria-label={`${row.insurerName} ID/PW 복사`}
                                title="ID/PW 복사"
                                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                                onClick={() => copyRow(row)}
                              >
                                {copiedKey === row.key ? (
                                  <Check className="h-3.5 w-3.5 text-brand-sky" aria-hidden="true" />
                                ) : (
                                  <Copy className="h-3.5 w-3.5" aria-hidden="true" />
                                )}
                              </button>
                            ) : (
                              <span className="h-7 w-7" aria-hidden="true" />
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function CredentialModal({ open, sectionId, item, initial, onClose, onAfterChange }) {
  const key = `${sectionId}::${String(item?.name || '')}`
  const [username, setUsername] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [showPw, setShowPw] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [err, setErr] = React.useState('')

  React.useEffect(() => {
    if (!open) return
    setUsername(String(initial?.username || ''))
    setPassword(String(initial?.password || ''))
    setErr('')
    setShowPw(false)
  }, [open, initial])

  if (!open || !item) return null

  const persist = async (remove) => {
    setSaving(true)
    setErr('')
    try {
      if (remove) {
        await api.delete('/me/insurer-system-credentials', { params: { systemKey: key } })
      } else {
        await api.put('/me/insurer-system-credentials', { systemKey: key, username, password })
      }
      await onAfterChange?.()
      onClose?.()
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || '저장에 실패했습니다.'
      setErr(String(msg))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="w-full max-w-md rounded-t-2xl border bg-white p-4 shadow-xl sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-lg font-bold text-slate-900">{item.name} 전산 계정</div>
          </div>
          <button type="button" className="rounded-lg px-2 py-1 text-sm text-slate-500 hover:bg-slate-50" onClick={onClose}>
            닫기
          </button>
        </div>
        {err ? <div className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700">{err}</div> : null}
        <label className="mt-4 block text-xs font-semibold text-slate-600">아이디(전산 ID)</label>
        <input
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          className="mt-1 w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand-sky/30"
          autoComplete="username"
        />
        <label className="mt-3 block text-xs font-semibold text-slate-600">비밀번호</label>
        <div className="mt-1 flex gap-2">
          <input
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            type={showPw ? 'text' : 'password'}
            className="w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand-sky/30"
            autoComplete="current-password"
          />
          <button
            type="button"
            className="shrink-0 rounded-xl border bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
            onClick={() => setShowPw((v) => !v)}
          >
            {showPw ? '숨기기' : '표시'}
          </button>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            className="rounded-full border bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40"
            disabled={!username}
            onClick={() => navigator.clipboard?.writeText(username)}
          >
            아이디 복사
          </button>
          <button
            type="button"
            className="rounded-full border bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40"
            disabled={!password}
            onClick={() => navigator.clipboard?.writeText(password)}
          >
            비밀번호 복사
          </button>
        </div>
        <div className="mt-5 flex items-center justify-between gap-3">
          <button
            type="button"
            className="text-sm font-semibold text-rose-700 hover:underline disabled:opacity-40"
            disabled={saving}
            onClick={() => persist(true)}
          >
            삭제
          </button>
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-full bg-brand-sky px-5 py-2 text-sm font-bold text-white hover:opacity-95 disabled:opacity-40"
            disabled={saving}
            onClick={() => persist(false)}
          >
            {saving ? '저장 중…' : '저장'}
          </button>
        </div>
      </div>
    </div>
  )
}

function InsurerTable({ section, items, isFavorite, onToggleFavorite, credByKey, onOpenCredential, resolveLogoUrl, isOperator }) {
  return (
    <div className="hidden md:block">
      <div className="overflow-x-auto rounded-xl border bg-white shadow-sm">
        <table className="min-w-[1100px] w-full text-sm">
          <thead className="sticky top-0 bg-slate-50 text-slate-700">
            <tr className="[&>th]:px-3 [&>th]:py-2 [&>th]:text-left [&>th]:font-semibold">
              <th className="w-[240px]">{section.firstColLabel}</th>
              <th className="w-[100px]">ID/PW</th>
              <th className="w-[120px]">고객센터</th>
              <th className="w-[140px]">인콜 모니터링</th>
              <th className="w-[170px]">전산 헬프데스크</th>
              <th className="w-[170px]">보험금 청구팩스</th>
              <th className="w-[80px]">약관확인</th>
              <th className="w-[80px]">청구서</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {items.map((it) => (
              <tr key={`${section.id}-${it.name}`} className="align-top hover:bg-slate-50/60">
                <td className="px-3 py-3">
                  <div className="flex items-center gap-3">
                    <a href={it.systemUrl} target="_blank" rel="noreferrer noopener" className="flex min-w-0 items-center gap-3">
                      <img
                        src={(resolveLogoUrl && resolveLogoUrl(it)) || it.logoUrl}
                        alt={it.name}
                        className="h-10 w-[72px] rounded-lg bg-white object-contain ring-1 ring-slate-200"
                        loading="lazy"
                      />
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-900">{it.name}</div>
                        <div className="text-xs text-slate-500">전산 접속</div>
                      </div>
                    </a>
                    <div className="ml-auto flex items-center gap-2">
                      {isOperator && it.insurerId ? (
                        <Link
                          to={`/data/insurers/${it.insurerId}`}
                          className="rounded-full border px-2.5 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                          onClick={(e) => e.stopPropagation()}
                        >
                          편집
                        </Link>
                      ) : null}
                      <StarButton
                        pressed={isFavorite(section.id, it)}
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          onToggleFavorite(section.id, it)
                        }}
                        label={isFavorite(section.id, it) ? '즐겨찾기 해제' : '즐겨찾기 추가'}
                      />
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3">
                  {(() => {
                    const k = `${section.id}::${String(it?.name || '')}`
                    const has = Boolean(credByKey?.[k]?.username || credByKey?.[k]?.password)
                    return (
                      <button
                        type="button"
                        onClick={() => onOpenCredential?.(section.id, it)}
                        className={
                          has
                            ? 'text-xs font-semibold text-brand-sky hover:underline'
                            : 'text-xs font-semibold text-slate-600 hover:text-brand-sky hover:underline'
                        }
                      >
                        {has ? '보기' : '등록'}
                      </button>
                    )
                  })()}
                </td>
                <td className="px-3 py-3"><LinkList items={it.customerCenter} /></td>
                <td className="px-3 py-3"><LinkList items={it.inCall} /></td>
                <td className="px-3 py-3"><LinkList items={it.helpDesk} /></td>
                <td className="px-3 py-3"><LinkList items={it.claimFax} /></td>
                <td className="px-3 py-3">
                  <ExternalButton href={it.termsUrl} label="열기" />
                </td>
                <td className="px-3 py-3">
                  <ExternalButton href={it.formUrl} label="열기" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function InsurerCards({ sectionId, items, isFavorite, onToggleFavorite, credByKey, onOpenCredential, resolveLogoUrl, isOperator }) {
  return (
    <div className="grid grid-cols-1 gap-3 md:hidden">
      {items.map((it) => (
        <div key={it.name} className="rounded-xl border bg-white p-3 shadow-sm">
          <div className="flex items-center gap-3">
            <a href={it.systemUrl} target="_blank" rel="noreferrer noopener" className="flex min-w-0 items-center gap-3">
              <img
                src={(resolveLogoUrl && resolveLogoUrl(it)) || it.logoUrl}
                alt={it.name}
                className="h-10 w-[72px] rounded-lg bg-white object-contain ring-1 ring-slate-200"
                loading="lazy"
              />
              <div className="min-w-0">
                <div className="truncate font-semibold text-slate-900">{it.name}</div>
                <div className="text-xs text-slate-500">전산 접속</div>
              </div>
            </a>
            <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
              {isOperator && it.insurerId ? (
                <Link
                  to={`/data/insurers/${it.insurerId}`}
                  className="inline-flex items-center justify-center rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50"
                >
                  편집
                </Link>
              ) : null}
              <StarButton
                pressed={isFavorite(sectionId, it)}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  onToggleFavorite(sectionId, it)
                }}
                label={isFavorite(sectionId, it) ? '즐겨찾기 해제' : '즐겨찾기 추가'}
              />
              <ExternalButton href={it.termsUrl} label="약관" />
              <ExternalButton href={it.formUrl} label="청구서" />
            </div>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
            <div className="col-span-2">
              <div className="text-xs font-semibold text-slate-500">ID/PW</div>
              <div className="mt-1">
                {(() => {
                  const k = `${sectionId}::${String(it?.name || '')}`
                  const has = Boolean(credByKey?.[k]?.username || credByKey?.[k]?.password)
                  return (
                    <button
                      type="button"
                      onClick={() => onOpenCredential?.(sectionId, it)}
                      className={
                        has
                          ? 'inline-flex items-center justify-center rounded-full bg-sky-50 px-3 py-1.5 text-xs font-semibold text-sky-800 ring-1 ring-sky-200 hover:bg-white'
                          : 'inline-flex items-center justify-center rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50'
                      }
                    >
                      {has ? '보기' : '등록'}
                    </button>
                  )
                })()}
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-500">고객센터</div>
              <div className="mt-1"><LinkList items={it.customerCenter} variant="inline" /></div>
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-500">인콜 모니터링</div>
              <div className="mt-1"><LinkList items={it.inCall} variant="inline" /></div>
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-500">전산 헬프데스크</div>
              <div className="mt-1"><LinkList items={it.helpDesk} variant="inline" /></div>
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-500">보험금 청구팩스</div>
              <div className="mt-1"><LinkList items={it.claimFax} variant="inline" /></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

/**
 * @param {{ variant?: 'standalone' | 'hub' }} props
 * standalone: 기존 보험사 전산 페이지와 동일
 * hub: 업무 허브 내 삽입용 카피·헤더 톤만 조정
 */
export default function InsurerSystemsDirectory({ variant = 'standalone' }) {
  const isHub = variant === 'hub'
  const { hasAnyRole } = useAuth()
  const isOperator = hasAnyRole && hasAnyRole([Roles.OPERATOR])
  const [q, setQ] = React.useState('')
  const [credByKey, setCredByKey] = React.useState({})
  const [systemProfiles, setSystemProfiles] = React.useState([])
  const [credModal, setCredModal] = React.useState(null)
  const [allCredModalOpen, setAllCredModalOpen] = React.useState(false)
  const [activeSectionId, setActiveSectionId] = React.useState('all')
  const [favorites, setFavorites] = React.useState(() => {
    try {
      const raw = window?.localStorage?.getItem(FAVORITES_STORAGE_KEY)
      const arr = raw ? JSON.parse(raw) : []
      const list = Array.isArray(arr) ? arr : []
      return new Set(list.map(migrateInsurerSystemStorageKey))
    } catch {
      return new Set()
    }
  })

  const needle = String(q || '').trim().toLowerCase().replace(/\s+/g, '')

  const keyOf = React.useCallback((sectionId, it) => {
    return `${sectionId}::${String(it?.name || '')}`
  }, [])

  const isFavorite = React.useCallback((sectionId, it) => {
    return favorites.has(keyOf(sectionId, it))
  }, [favorites, keyOf])

  const toggleFavorite = React.useCallback((sectionId, it) => {
    const k = keyOf(sectionId, it)
    setFavorites((prev) => {
      const next = new Set(prev)
      if (next.has(k)) next.delete(k)
      else next.add(k)
      return next
    })
  }, [keyOf])

  React.useEffect(() => {
    try {
      window?.localStorage?.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(Array.from(favorites)))
    } catch { }
  }, [favorites])

  const [orgOptionsForLogo, setOrgOptionsForLogo] = React.useState([])

  React.useEffect(() => {
    let ignore = false
    ;(async () => {
      try {
        const res = await api.get('/orgs/options', { params: { scope: isOperator ? 'all' : 'mine' } })
        const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        const rows = Array.isArray(payload) ? payload : []
        if (!ignore) setOrgOptionsForLogo(rows)
      } catch {
        if (!ignore) setOrgOptionsForLogo([])
      }
    })()
    return () => { ignore = true }
  }, [isOperator])

  React.useEffect(() => {
    let ignore = false
    ;(async () => {
      try {
        const res = await api.get('/insurer-system-profiles', { __silent: true })
        const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        const items = Array.isArray(payload) ? payload : (payload?.items || [])
        if (!ignore) setSystemProfiles(Array.isArray(items) ? items : [])
      } catch {
        if (!ignore) setSystemProfiles([])
      }
    })()
    return () => { ignore = true }
  }, [])

  const resolveLogoUrl = React.useCallback(
    (it) => resolveInsurerLogoUrlFromOrgOptions(it?.name, orgOptionsForLogo),
    [orgOptionsForLogo],
  )

  const loadCredentials = React.useCallback(async () => {
    try {
      const res = await api.get('/me/insurer-system-credentials', { __silent: true })
      const data = res?.data?.data
      const items = Array.isArray(data?.items) ? data.items : []
      const next = {}
      for (const it of items) {
        const k = String(it?.systemKey || '')
        if (!k) continue
        next[k] = { username: String(it?.username || ''), password: String(it?.password || '') }
      }
      setCredByKey(next)
    } catch {
      // ignore
    }
  }, [])

  React.useEffect(() => {
    loadCredentials()
  }, [loadCredentials])

  const sections = React.useMemo(() => {
    const mergedSections = mergeInsurerSystemSections(INSURER_SYSTEMS_SECTIONS, systemProfiles, needle, favorites)
    if (activeSectionId === 'all') return mergedSections
    return mergedSections.filter((sec) => sec.id === activeSectionId)
  }, [activeSectionId, needle, favorites, systemProfiles])

  const allCredentialSections = React.useMemo(() => {
    const allSections = mergeInsurerSystemSections(INSURER_SYSTEMS_SECTIONS, systemProfiles, '', favorites)
    return allSections.map((sec) => ({
      id: sec.id,
      title: sec.title,
      items: (sec.items || []).map((it) => {
        const k = `${sec.id}::${String(it?.name || '')}`
        const cred = credByKey[k] || {}
        return {
          key: k,
          sectionId: sec.id,
          insurerName: it.name,
          item: it,
          username: String(cred.username || ''),
          password: String(cred.password || ''),
        }
      }),
    }))
  }, [systemProfiles, favorites, credByKey])

  const totalCount = sections.reduce((acc, s) => acc + (s.items?.length || 0), 0)
  const registeredCredCount = allCredentialSections
    .flatMap((sec) => sec.items || [])
    .filter((r) => r.username || r.password).length

  const TitleTag = isHub ? 'h2' : 'h1'

  return (
    <div className="space-y-5">
      <div
        className={
          isHub
            ? 'rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 p-4 shadow-sm'
            : 'rounded-2xl border bg-gradient-to-br from-slate-50 to-white p-4 shadow-sm'
        }
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <TitleTag className="text-xl font-bold tracking-tight text-slate-900">
                {isHub ? '원수사 연결·전산' : '보험사 전산'}
              </TitleTag>
              {!isHub ? (
                <MenuGuideButton slug="insurer-systems" title="업무지원 가이드" />
              ) : null}
              {isHub ? (
                <Link
                  to="/work/insurer-systems"
                  className="text-xs font-semibold text-brand-navy underline-offset-2 hover:underline"
                >
                  전산 전용 화면
                </Link>
              ) : null}
              {!isHub && isOperator ? (
                <Link
                  to="/work/insurer-business-hub"
                  className="text-xs font-semibold text-indigo-800 underline-offset-2 hover:underline"
                >
                  업무 허브
                </Link>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => setAllCredModalOpen(true)}
              className="inline-flex items-center justify-center rounded-full border bg-white px-4 py-2 text-sm font-semibold text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50"
            >
              ID/PW 전체보기
              {registeredCredCount > 0 ? (
                <span className="ml-2 rounded-full bg-sky-50 px-2 py-0.5 text-xs font-semibold text-sky-800 ring-1 ring-sky-200">
                  {registeredCredCount}
                </span>
              ) : null}
            </button>
          </div>
          <div className="w-full sm:w-80">
            <label className="mb-1 block text-xs font-semibold text-slate-500">보험사 검색</label>
            <div className="relative">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400"
              >
                <path d="M21 21l-4.3-4.3" />
                <circle cx="11" cy="11" r="7" />
              </svg>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="예) 삼성, KB, 한화…"
                className="w-full rounded-xl border bg-white py-2.5 pl-10 pr-3 text-sm shadow-sm outline-none focus:ring-2 focus:ring-brand-sky/30"
              />
            </div>
            <div className="mt-1 text-[11px] text-slate-400">검색 결과: {totalCount}건</div>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="inline-flex min-w-full gap-2 rounded-xl border bg-white p-1 shadow-sm sm:min-w-0">
          {INSURER_TYPE_TABS.map((tab) => {
            const selected = activeSectionId === tab.id
            return (
              <button
                key={tab.id}
                type="button"
                aria-pressed={selected}
                onClick={() => setActiveSectionId(tab.id)}
                className={
                  selected
                    ? 'whitespace-nowrap rounded-lg bg-brand-navy px-4 py-2 text-sm font-bold text-white shadow-sm'
                    : 'whitespace-nowrap rounded-lg px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }
              >
                {tab.label}
              </button>
            )
          })}
        </div>
      </div>

      {totalCount === 0 && (
        <div className="rounded-xl border bg-white p-5 text-sm text-slate-700 shadow-sm">
          검색 결과가 없습니다.
        </div>
      )}

      {sections.map((sec) => {
        if (!sec.items || sec.items.length === 0) return null
        return (
          <section key={sec.id} className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-brand-sky/70" />
                <h3 className="text-lg font-bold text-slate-900">[{sec.title}]</h3>
              </div>
              <span className="text-xs text-slate-500">{sec.items.length}개</span>
            </div>
            <InsurerTable
              section={sec}
              items={sec.items}
              isFavorite={isFavorite}
              onToggleFavorite={toggleFavorite}
              credByKey={credByKey}
              onOpenCredential={(sectionId, it) => setCredModal({ sectionId, item: it })}
              resolveLogoUrl={resolveLogoUrl}
              isOperator={isOperator}
            />
            <InsurerCards
              sectionId={sec.id}
              items={sec.items}
              isFavorite={isFavorite}
              onToggleFavorite={toggleFavorite}
              credByKey={credByKey}
              onOpenCredential={(sectionId, it) => setCredModal({ sectionId, item: it })}
              resolveLogoUrl={resolveLogoUrl}
              isOperator={isOperator}
            />
          </section>
        )
      })}

      <div className="rounded-xl border bg-white p-4 text-xs leading-relaxed text-slate-500 shadow-sm">
        {isHub ? (
          <p>
            링크·번호는 참고용이며 보험사 정책에 따라 변경될 수 있습니다. 전산 ID/PW 정보는 본인만 조회할 수 있습니다.
            {' '}
            <Link className="font-semibold text-brand-navy underline-offset-2 hover:underline" to="/work/insurer-systems">
              전산 단독 페이지
            </Link>
            에서도 동일 목록을 사용합니다.
          </p>
        ) : (
          <>
            - 링크/연락처는 제공받은 자료 기준으로 구성되어 있으며, 보험사 정책/상품에 따라 변경될 수 있습니다. <br />
            - 전산 접속은 각 보험사 로그인/보안 프로그램 설치가 필요할 수 있습니다. <br />
            - 「ID/PW」에 입력한 아이디·비밀번호는 로그인한 본인만 조회·수정할 수 있으며 서버에 저장됩니다.
          </>
        )}
      </div>

      <CredentialModal
        open={Boolean(credModal)}
        sectionId={credModal?.sectionId}
        item={credModal?.item}
        initial={credModal ? credByKey[`${credModal.sectionId}::${String(credModal.item?.name || '')}`] : null}
        onClose={() => setCredModal(null)}
        onAfterChange={loadCredentials}
      />

      <AllCredentialsModal
        open={allCredModalOpen}
        sections={allCredentialSections}
        onClose={() => setAllCredModalOpen(false)}
        onOpenCredential={(sectionId, it) => setCredModal({ sectionId, item: it })}
      />
    </div>
  )
}
